import { Columns03Icon } from '@langchain/untitled-ui-icons';
import { Checkbox } from '@mui/joy';
import * as Popover from '@radix-ui/react-popover';

import { ReactNode, useCallback, useMemo } from 'react';

import { Button, IconButton, Tooltip } from '@/design-system';
import { DisplaySettings, SessionSchema } from '@/types/schema';
import { capitalize } from '@/utils/stringUtils';
import { cn } from '@/utils/tailwind';

import { DatasetSessionCompareSettings, METRICS_COLUMNS } from '../constants';
import { isInPlayground } from '../utils/isInPlayground';
import { useColumnSelection } from './hooks/useColumnSelection';

export enum ColumnCategoryId {
  EXAMPLE_COLUMNS = 'example_columns',
  FEEDBACK_COLUMNS = 'feedback_columns',
  METRICS_COLUMNS = 'metrics_columns',
}

type ColumnCategory = {
  id: ColumnCategoryId;
  name: ReactNode;
  items: DisplaySettings[];
};

const DEFAULT_VISIBLE_CATEGORIES = [
  ColumnCategoryId.EXAMPLE_COLUMNS,
  ColumnCategoryId.FEEDBACK_COLUMNS,
  ColumnCategoryId.METRICS_COLUMNS,
];

export const ColumnVisibilityPopover = ({
  displaySettingsState,
  aggregateFeedbackKeys,
  noAttachments,
  sessions,
  visibleCategories = DEFAULT_VISIBLE_CATEGORIES,
}: {
  displaySettingsState: DatasetSessionCompareSettings;
  aggregateFeedbackKeys: string[];
  noAttachments: boolean;
  sessions: Pick<SessionSchema, 'id' | 'name'>[];
  visibleCategories?: ColumnCategoryId[];
}) => {
  const inPlayground = isInPlayground();
  const {
    isReferenceInputHidden,
    setIsReferenceInputHidden,
    isReferenceOutputHidden,
    setIsReferenceOutputHidden,
    canResetDefaults,
    onResetDefaults,
    hiddenFeedbackColumns,
    setHiddenFeedbackColumns,
    hiddenMetricsColumns,
    setHiddenMetricsColumns,
    isAttachmentsHidden,
    setIsAttachmentsHidden,
  } = displaySettingsState;

  const numSessions = sessions.length;

  const numIOColumns = 3;
  const numIOColumnsSelected = [
    !isReferenceInputHidden,
    !isReferenceOutputHidden,
    !isAttachmentsHidden,
  ].filter(Boolean).length;
  const allIOColumnsSelected = numIOColumnsSelected === numIOColumns;
  const toggleAllIOColumns = useCallback(
    (selected: boolean) => {
      setIsReferenceInputHidden(!selected);
      setIsReferenceOutputHidden(!selected);
      setIsAttachmentsHidden(!selected);
    },
    [
      setIsReferenceInputHidden,
      setIsReferenceOutputHidden,
      setIsAttachmentsHidden,
    ]
  );

  const {
    numColumns: numFeedbackColumns,
    numColumnsSelected: numFeedbackColumnsSelected,
    allSelected: allFeedbackSelected,
    toggleColumn: toggleFeedbackColumn,
    toggleAllColumns: toggleAllFeedbackColumns,
  } = useColumnSelection(
    hiddenFeedbackColumns,
    setHiddenFeedbackColumns,
    aggregateFeedbackKeys
  );

  const {
    numColumns: numMetricsColumns,
    numColumnsSelected: numMetricsColumnsSelected,
    allSelected: allMetricsSelected,
    toggleColumn: toggleMetricColumn,
    toggleAllColumns: toggleAllMetricsColumns,
  } = useColumnSelection(
    hiddenMetricsColumns,
    setHiddenMetricsColumns,
    Object.values(METRICS_COLUMNS).map((metric) => metric.name)
  );

  const categorizedSettings: ColumnCategory[] = useMemo(
    () => [
      ...(visibleCategories.includes(ColumnCategoryId.EXAMPLE_COLUMNS)
        ? [
            {
              id: ColumnCategoryId.EXAMPLE_COLUMNS,
              name: (
                <div className="flex flex-row gap-2">
                  <Tooltip
                    title={
                      allIOColumnsSelected
                        ? 'Unselect all columns'
                        : 'Select all columns'
                    }
                  >
                    <Checkbox
                      checked={allIOColumnsSelected}
                      indeterminate={
                        numIOColumnsSelected > 0 &&
                        numIOColumnsSelected < numIOColumns
                      }
                      onChange={(e) => toggleAllIOColumns(e.target.checked)}
                    />
                  </Tooltip>
                  <span className="text-sm font-medium">
                    {numSessions === 1 ? 'Example' : 'Columns'}
                  </span>
                </div>
              ),
              items: [
                {
                  name: 'Input',
                  selected: !isReferenceInputHidden,
                  onSelect: () => setIsReferenceInputHidden((prev) => !prev),
                },
                ...(noAttachments
                  ? []
                  : [
                      {
                        name: 'Attachments',
                        selected: !isAttachmentsHidden,
                        onSelect: () => setIsAttachmentsHidden((prev) => !prev),
                      },
                    ]),
                {
                  name: 'Reference Output',
                  selected: !isReferenceOutputHidden,
                  onSelect: () => setIsReferenceOutputHidden((prev) => !prev),
                },
              ],
            },
          ]
        : []),
      ...(aggregateFeedbackKeys.length > 0 &&
      visibleCategories.includes(ColumnCategoryId.FEEDBACK_COLUMNS)
        ? [
            {
              id: ColumnCategoryId.FEEDBACK_COLUMNS,
              name: (
                <div className="flex flex-row gap-2">
                  <Tooltip
                    title={
                      allFeedbackSelected
                        ? 'Unselect all feedback columns'
                        : 'Select all feedback columns'
                    }
                  >
                    <Checkbox
                      checked={allFeedbackSelected}
                      indeterminate={
                        numFeedbackColumnsSelected > 0 &&
                        numFeedbackColumnsSelected < numFeedbackColumns
                      }
                      onChange={(e) =>
                        toggleAllFeedbackColumns(e.target.checked)
                      }
                    />
                  </Tooltip>
                  <span className="text-sm font-medium">Feedback</span>
                </div>
              ),
              items: aggregateFeedbackKeys.map((feedbackKey) => ({
                name: capitalize(feedbackKey),
                selected: !hiddenFeedbackColumns.includes(feedbackKey),
                onSelect: () =>
                  toggleFeedbackColumn(
                    feedbackKey,
                    !hiddenFeedbackColumns.includes(feedbackKey)
                  ),
              })),
            },
          ]
        : []),
      ...(visibleCategories.includes(ColumnCategoryId.METRICS_COLUMNS)
        ? [
            {
              id: ColumnCategoryId.METRICS_COLUMNS,
              name: (
                <div className="flex flex-row gap-2">
                  <Tooltip
                    title={
                      allMetricsSelected
                        ? 'Unselect all metrics'
                        : 'Select all metrics'
                    }
                  >
                    <Checkbox
                      checked={allMetricsSelected}
                      indeterminate={
                        numMetricsColumnsSelected > 0 &&
                        numMetricsColumnsSelected < numMetricsColumns
                      }
                      onChange={(e) =>
                        toggleAllMetricsColumns(e.target.checked)
                      }
                    />
                  </Tooltip>
                  <span className="text-sm font-medium">Metrics</span>
                </div>
              ),
              items: Object.values(METRICS_COLUMNS).map((metric) => ({
                name: capitalize(metric.name),
                selected: !hiddenMetricsColumns.includes(metric.name),
                onSelect: () =>
                  toggleMetricColumn(
                    metric.name,
                    !hiddenMetricsColumns.includes(metric.name)
                  ),
              })),
            },
          ]
        : []),
    ],
    [
      visibleCategories,
      allIOColumnsSelected,
      numIOColumnsSelected,
      numSessions,
      isReferenceInputHidden,
      noAttachments,
      isAttachmentsHidden,
      isReferenceOutputHidden,
      aggregateFeedbackKeys,
      allFeedbackSelected,
      numFeedbackColumnsSelected,
      numFeedbackColumns,
      allMetricsSelected,
      numMetricsColumnsSelected,
      numMetricsColumns,
      toggleAllIOColumns,
      setIsReferenceInputHidden,
      setIsAttachmentsHidden,
      setIsReferenceOutputHidden,
      toggleAllFeedbackColumns,
      hiddenFeedbackColumns,
      toggleFeedbackColumn,
      toggleAllMetricsColumns,
      hiddenMetricsColumns,
      toggleMetricColumn,
    ]
  );

  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        {inPlayground ? (
          <IconButton
            color="secondary"
            label="Select columns"
            icon={Columns03Icon}
            size="md"
          />
        ) : (
          <Button
            color="secondary"
            variant="outlined"
            size="md"
            leftDecorator={Columns03Icon}
          >
            Display
          </Button>
        )}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          sideOffset={5}
          align="end"
          className="relative z-[999]"
        >
          <div className="flex flex-col rounded-md border border-secondary bg-popover shadow-lg dark:border-tertiary">
            <div className="flex max-h-[500px] w-[300px] flex-col overflow-auto pb-3">
              {categorizedSettings.map((category, categoryIdx) => (
                <CategorySection
                  key={categoryIdx}
                  name={category.name}
                  categoryIdx={numSessions > 1 ? categoryIdx + 1 : categoryIdx}
                >
                  <div className="flex flex-col gap-2 px-4">
                    {category.items.map((column, idx) => (
                      <Checkbox
                        key={idx}
                        checked={column.selected}
                        onChange={column.onSelect}
                        label={column.name}
                      />
                    ))}
                  </div>
                </CategorySection>
              ))}
            </div>
            <hr className="flex-shrink-0 border-secondary" />
            {canResetDefaults &&
              visibleCategories.length ===
                DEFAULT_VISIBLE_CATEGORIES.length && (
                <button
                  type="button"
                  className="mx-2 my-2 rounded-md px-2 py-1 text-left text-sm text-tertiary transition-all hover:bg-secondary-hover active:bg-secondary"
                  onClick={onResetDefaults}
                >
                  Reset to default
                </button>
              )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

const CategorySection = ({
  name,
  children,
  categoryIdx,
}: {
  name: ReactNode;
  children: ReactNode;
  categoryIdx: number;
}) => {
  return (
    <div key={categoryIdx}>
      <div
        className={cn(
          'mb-2 flex items-center gap-2 bg-secondary-hover px-4 py-1 font-medium text-tertiary',
          categoryIdx !== 0 && 'mt-2'
        )}
      >
        <span className="text-xs uppercase text-tertiary">{name}</span>
      </div>
      {children}
    </div>
  );
};
